import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ConfirmationService } from "primeng/api";
import { Table } from "primeng/table";
import { User } from "src/app/core/endpoints/models/user.model";
import { ApiService } from "src/app/core/services/api.service";
import { ToastService } from "src/app/core/services/toast.service";
import { confirmPassword } from "src/app/core/utils/utils";
import { ImageCroppedEvent, LoadedImage } from "ngx-image-cropper";
import { Router } from "@angular/router";
import { AuthenticationService } from "src/app/core/services/authentication.service";

@Component({
	selector: "app-users",
	templateUrl: "./users.component.html",
	styleUrls: ["./users.component.scss"],
})
export class UsersComponent implements OnInit {
	@ViewChild("dt") table: Table;
	@ViewChild("filter") filter: ElementRef;

	imageChangedEvent: Event | null = null;
	croppedActive: boolean = false;

	public user: any;
	public role_ids: any[] | undefined;
	public users: User[] = [];
	public communities: any[] = [];
	public loading: boolean = false;
	public dialogUser: boolean = false;
	public mode: "edit" | "register" = "edit";
	public photo: File;
	public stringPhoto: string = null;
	public userSelected: User;
	public isCodeDisabled: boolean = false;
	public loggedUser: any;

	form: FormGroup = this.fb.group({
		code: ["", [Validators.required, Validators.pattern("^[a-zA-Z0-9]+$")]],
		identification: ["", [Validators.required]],
		name: [
			"",
			[Validators.required, Validators.pattern("^[a-zA-ZÁáÉéÍíÓóÚúÑñ1234567890 ]*$")],
		],
		email: ["", [Validators.email]],
		password: [""],
		password_confirmation: [""],
		phone: ["", [Validators.pattern("^[0-9]+$")]],
		role_id: ["", [Validators.required]],
		avatar: [""]
	});

	constructor(
		private apiService: ApiService,
		private fb: FormBuilder,
		private toast: ToastService,
		private confirmationService: ConfirmationService,
		public router: Router,
		public authService: AuthenticationService
	) {
		this.loggedUser = this.authService.getUser();
	}

	ngOnInit(): void {
		this.role_ids = [{ name: "Seleccione", value: null }];
		this.getUser();
	}

	getUser() {
		this.user = JSON.parse(localStorage.getItem("dataUserWeb"));
		this.role_ids = [];
		this.form.patchValue({ role_id: 2 });
		this.form.get("role_id")?.updateValueAndValidity();
		this.loadUsers();
	}
	
	public loadUsers() {
		this.loading = true;
		this.apiService.getEntity("usersAll").subscribe({
			next: (result) => {
				this.users = result.users.map((user) => ({
					...user,
					role_id_string:
						user.role_id === 1
							? "Admin"
							: "Cliente",
					status_string: user.status === 1 ? "Activo" : user.status === 2 ? "Esperando registro" : user.status === 0 ? "Desactivado" : "Eliminado",
				}));
			},
			error: (err) => {
				this.loading = false;
				console.log(err);
			},
			complete: () => {
				this.loading = false;
			},
		});
	}

	public clear(table: Table) {
		table.clear();
		this.filter.nativeElement.value = "";
	}

	public registerUser() {
		this.form.markAllAsTouched();
		if (this.form.invalid) {
			return this.toast.show("Aviso", "Revise los datos del formulario", "warn");
		}

		this.loading = true;
		this.form.patchValue({ avatar: this.stringPhoto });
		this.apiService.addEntity("registerUserFromAdmin", this.form.value).subscribe({
			next: (result) => {
				this.dialogUser = false;
				this.toast.show("Usuario creado correctamente");
				this.loadUsers();
				this.croppedActive = false;
			},
			error: (err) => {
				let message = "Ocurrió un error al crear el usuario, inténtelo de nuevo";
				if (err && err.error && err.error.errors) {
					message = "";
					let errors = err.error.errors;
					for (const field in errors) {
						errors[field].forEach((errorMessage) => {
							message += `${errorMessage}\n`;
						});
					}
				}
				this.loading = false;
				this.toast.show("Error", message, "error");
			},
			complete: () => {
				this.loading = false;
			},
		});
	}

	public updateUser() {
		this.form.markAllAsTouched();
		if (this.form.invalid) {
			return this.toast.show("Aviso", "Revise el formulario", "warn");
		}

		this.loading = true;
		this.form.patchValue({ avatar: this.stringPhoto });
		this.apiService.updateEntity("updateUserFromAdmin", this.userSelected.id, this.form.value).subscribe({
			next: (result) => {
				this.loading = false;
				this.toast.show("Usuario actualizado correctamente");
				this.dialogUser = false;
				this.loadUsers();
				this.croppedActive = false;
				this.resetForm();
			},
			error: (err) => {
				this.loading = false;
				let message = "Ocurrió un error al editar el usuario, inténtelo de nuevo";
				if (err && err.error && err.error.errors) {
					message = "";
					let errors = err.error.errors;
					for (const field in errors) {
						errors[field].forEach((errorMessage) => {
							message += `${errorMessage}\n\r`;
						});
					}
				}
				this.loading = false;
				this.toast.show("Error", message, "error");
			},
			complete: () => {
				this.loading = false;
			},
		});
	}

	public deleteUser(user: User) {
		if (this.loading) return;
		this.confirmationService.confirm({
			target: event.target,
			message: "¿Está seguro que desea eliminar el usuario?",
			icon: "pi pi-exclamation-triangle",
			accept: () => {
				this.loading = true;
				this.apiService.deleteEntity("deleteUserFromAdmin", user.id).subscribe({
					next: () => {
						this.loading = false;
						this.toast.show("Usuario eliminado correctamente");
						this.loadUsers();
					},
					error: () => {
						this.loading = false;
						this.toast.show("Error", "Ocurrió un error eliminando el usuario, inténtelo de nuevo", "error");
					},
				});
			},
		});
	}

	public showDialog(user?: User) {
		this.isCodeDisabled = false;
		this.userSelected = undefined;
		if (user) {
			this.userSelected = user;
			this.form.patchValue(user);
			if (user.avatar) {
				this.stringPhoto = user.avatar;
			}
			if (user.codes) {
				this.isCodeDisabled = true;
				this.form.patchValue({ code: user.codes.code });
			}
			if (user.email.includes('makemark.nytelweb.com')){
				this.form.patchValue({ email: null });
			}
		}
		this.mode = user ? "edit" : "register";
		this.dialogUser = true;
	}

	public updateStatus(user: User) {
		this.loading = true;
		this.apiService.updateEntity("updateUserStatusFromAdmin", user.id, {
			user: user,
			status: user.status === 0 ? 1 : 0,
		}).subscribe({
			next: () => {
				this.loading = false;
			},
			error: (err) => {
				console.log(err);
			},
			complete: () => {
				this.loadUsers();
				this.loading = false;
			},
		});
	}

	public deletePhoto() {
		this.stringPhoto = undefined;
		this.croppedActive = false;
	}

	public resetForm() {
		this.form.reset();
		this.stringPhoto = undefined;
		this.form.patchValue({ role_id: 2 });
	}

	public handleUpload($event: any) {
		this.imageChangedEvent = $event;
		this.croppedActive = true;
	}

	imageCropped(event: ImageCroppedEvent) {
		this.stringPhoto = event.base64;
	}
	imageLoaded(image: LoadedImage) {}
	cropperReady() {}
	loadImageFailed() {}

	
	public investmentsUser(user: User) {
		localStorage.setItem('userId',user.id);
		setTimeout(() => {
		  if (user) {
			this.router.navigate(['app/investments-user']);
		  }
		}, 250);
	}

}
